import moment from 'moment'

import { DateUtils, ObjectUtils } from '../../../utils'

/**
 * Calculates number of interactions
 * @param {Number} users Number of unique users
 * @param {Number} devices Number of devices
 */
const calculateInteractions = (users, devices) => {
    const interactions = users / devices

    return Number.isFinite(interactions) ? interactions : 0
}

/**
 * Extracts the labels and data from the activity count grouped by days
 * @param {Array<Object>} activityCount
 * @param {Number} activityCount.day
 * @param {Number} activityCount.hints
 */
const activityCountByDays = (activityCount) => {
    const data = []
    const labels = []

    // Indexes hits by days
    const indexed = activityCount.reduce((out, { day, hits }) => ({ ...out, [day]: hits }), {})

    // Current date
    let date = new Date()
    date.setMinutes(0)

    // Iterate while `day` exists in indexed map
    while (!ObjectUtils.isObjValuesEmpty(indexed)) {
        // Gets date's day
        const day = date.getDate()

        // Adds the day at the end of the labels array
        const m = moment(date)
        labels.unshift(m.format('DD MMM'))

        // Adds the number of hints at the end of the data array (0 if not record on that day)
        const value = indexed[day] ? indexed[day] : 0
        data.unshift(value)

        // Remove item from the indexed map
        indexed[day] = null

        // Next day to check
        date = new Date(DateUtils.minusDays(date, 1))
    }

    return { data, labels }
}

/**
 * Extracts the labels and data from the activity count grouped by hours
 * @param {Array<Object>} activityCount
 * @param {Number} activityCount.hour
 * @param {Number} activityCount.hints
 */
const activityCountByHours = (activityCount) => {
    const data = []
    const labels = []

    // Indexes hits by hours
    const indexed = activityCount.reduce((out, { hour, hits }) => ({ ...out, [hour]: hits }), {})

    // Current date
    let date = new Date()
    date.setMinutes(0)

    // Iterate while `day` exists in indexed map
    while (!ObjectUtils.isObjValuesEmpty(indexed)) {
        // Gets date's hour
        const hour = date.getHours()

        // Adds the hour at the end of the labels array
        const m = moment(date)
        labels.unshift(m.format('H:mm'))

        // Adds the number of hints at the end of the data array (0 if not record on that hour)
        const value = indexed[hour] ? indexed[hour] : 0
        data.unshift(value)

        // Remove item from the indexed map
        indexed[hour] = null

        // Next day to check
        date = new Date(DateUtils.minusMinutes(date, 60))
    }

    return { data, labels }
}

/**
 * Extracts the labels and data from the activity count grouped by hours or by day
 * @param {Array<Object>} activityCount
 */
const buildActivityCount = (activityCount = []) => {
    const out = {
        data: [],
        labels: [],
    }

    if (!activityCount.length) {
        return out
    }

    // If the first item is indexed by day, process the whole array by days.
    if (activityCount[0].day) {
        return activityCountByDays(activityCount)
    }

    return activityCountByHours(activityCount)
}

/**
 * Calculates required statistics for top stores list
 * @param {Array} topStores
 * @param {Array} prevTopStores
 */
const buildTopStores = (topStoresArray, prevTopStoresArray) => {
    const prevMapped = prevTopStoresArray.reduce((a, b, index) => ({ ...a, [b.store_code]: { ...b, index } }), {})

    // Iterate all stores
    const topStoresTrans = topStoresArray.map((store, currentIndex) => {
        // Store items
        const { unique_visitors: users, deviceCount: devices, store_code } = store

        // Calculate current interactions
        const interactions = calculateInteractions(users, devices)

        // Get previous store data
        const prevStore = prevMapped[store_code]

        // By default, delta is 1, because if previous store is not defined,
        // it means this store has got a better position
        let delta = 1

        // Calculate delta, if previous store exists
        if (prevStore) {
            const { index: prevIndex } = prevStore
            delta = prevIndex - currentIndex
        }

        return { ...store, delta, interactions }
    })

    return topStoresTrans
}

/**
 * Calculates required statistics for top brands list
 * @param {Array} topBrandsArray
 * @param {Array} prevTopStores
 */
const buildTopBrands = (topBrandsArray, prevTopBrandsArray) => {
    const prevMapped = prevTopBrandsArray.reduce((a, b, index) => ({ ...a, [b.brand]: { ...b, index } }), {})

    // Iterate all brands
    const topBrandsTrans = topBrandsArray.map((brand, currentIndex) => {
        // Brand items
        const { brand: brandName } = brand

        // Get previous brand data
        const prevBrand = prevMapped[brandName]

        // By default, delta is 1, because if previous brand is not defined,
        // it means this brand has got a better position
        let delta = 1

        // Calculate delta, if previous brand exists
        if (prevBrand) {
            const { index: prevIndex } = prevBrand
            delta = prevIndex - currentIndex
        }

        return { ...brand, delta }
    })

    return topBrandsTrans
}

/**
 * Calculates required statistics for top models list
 * @param {Array} topModelsArray
 * @param {Array} prevTopModels
 */
const buildTopModels = (topModelsArray, prevTopModels) => {
    const prevMapped = prevTopModels.reduce((a, b, index) => ({ ...a, [b.model]: { ...b, index } }), {})

    // Iterate all models
    const topModelsTrans = topModelsArray.map((model, currentIndex) => {
        // Model items
        const { model: modelName } = model

        // Get previous model data
        const prevModel = prevMapped[modelName]

        // By default, delta is 1, because if previous model is not defined,
        // it means this model has got a better position
        let delta = 1

        // Calculate delta, if previous model exists
        if (prevModel) {
            const { index: prevIndex } = prevModel
            delta = prevIndex - currentIndex
        }

        return { ...model, delta }
    })

    return topModelsTrans
}

export { buildActivityCount, buildTopBrands, buildTopModels, buildTopStores, calculateInteractions }
